<template>
  <div :class="$attrs.class" :style="[groupContainerStyle, $attrs.style as any]">
    <!-- header -->
    <div
      ref="headerRef"
      :style="{
        display: 'flex',
        width: isTitleOnTop ? '100%' : undefined,
        alignItems: isTitleOnTop ? undefined : 'stretch',
      }"
    >
      <GroupHeader
        :title="title"
        :description="isTitleOnTop ? description : undefined"
        :extra="isTitleOnTop ? extra : undefined"
        :title-placement="titlePlacement"
        :title-align="titleAlign"
        :collapsible="collapsible"
        :collapsed="collapsed"
        :group-title-width="groupTitleWidth"
        :theme="theme"
        :size="size"
        @toggle="collapsed = !collapsed"
      >
        <template v-if="$slots.title" #title>
          <slot name="title" />
        </template>
        <template v-if="isTitleOnTop && $slots.description" #description>
          <slot name="description" />
        </template>
        <template v-if="isTitleOnTop && $slots.extra" #extra>
          <slot name="extra" />
        </template>
      </GroupHeader>
    </div>

    <!-- content viewport -->
    <div v-if="$slots.default" :style="contentViewportStyle" :aria-hidden="collapsed">
      <!-- scrollable content -->
      <JdScrollbar
        v-if="hasScrollableContent"
        ref="contentScrollbarRef"
        :wrap-style="contentScrollbarWrapStyle"
      >
        <div
          ref="contentRef"
          :style="{
            flex: shouldFillWidth ? 1 : undefined,
            minWidth: 0,
            width: isTitleOnTop ? (shouldFillWidth ? '100%' : 'fit-content') : undefined,
            maxWidth: '100%',
          }"
        >
          <slot />
        </div>
      </JdScrollbar>

      <!-- non-scrollable content -->
      <div v-else ref="contentViewportRef" :style="{ overflowX: 'hidden', overflowY: 'hidden' }">
        <div
          ref="contentRef"
          :style="{
            flex: shouldFillWidth ? 1 : undefined,
            minWidth: 0,
            width: isTitleOnTop ? (shouldFillWidth ? '100%' : 'fit-content') : undefined,
            maxWidth: '100%',
          }"
        >
          <slot />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  onUpdated,
  provide,
  ref,
  watch,
  type CSSProperties,
} from 'vue'
import JdScrollbar from '@jdesign-vue/components/scrollbar'
import { useNamespace } from '@jdesign-vue/hooks'
import {
  statisticGroupKey,
  statisticCardAppearanceKey,
} from '../tokens'
import { CARD_GAP, createColorVars } from '../constants'
import GroupHeader from '../supporting/group-header.vue'
import type { StatisticCardSize, StatisticCardAppearanceProps, StatisticVNode } from '../types'

defineOptions({ name: 'JdStatisticCardGroup', inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    title?: StatisticVNode
    description?: StatisticVNode
    extra?: StatisticVNode
    size?: StatisticCardSize
    titlePlacement?: 'top' | 'left'
    titleAlign?: 'left' | 'center'
    collapsible?: boolean
    defaultCollapsed?: boolean
    groupTitleWidth?: number
    theme?: 'default' | 'brand' | (string & {})
    widthMode?: 'content' | 'container'
    maxWidth?: number
    maxHeight?: number
    reverseColor?: boolean
    border?: boolean
    equalHeight?: boolean
    numberScroll?: boolean
    subIndicatorsLayout?: 'horizontal' | 'vertical'
  }>(),
  {
    size: 'large',
    titlePlacement: 'top',
    titleAlign: 'left',
    collapsible: false,
    defaultCollapsed: false,
    theme: 'default',
    widthMode: 'content',
    border: false,
    equalHeight: undefined,
    reverseColor: undefined,
    numberScroll: undefined,
  },
)

const ns = useNamespace('statistic-card')
const colorVars = createColorVars(ns.cssVarName)

const collapsed = ref(props.defaultCollapsed)
const contentRef = ref<HTMLDivElement>()
const contentViewportRef = ref<HTMLDivElement>()
const contentScrollbarRef = ref<InstanceType<typeof JdScrollbar> | null>(null)
const headerRef = ref<HTMLDivElement>()

const contentHeight = ref(0)
const measuredHeaderHeight = ref(0)

const isTitleOnTop = computed(() => props.titlePlacement === 'top')
const shouldFillWidth = computed(() => props.widthMode === 'container')
const hasScrollableContent = computed(() => props.maxWidth != null || props.maxHeight != null)

const background = computed(() => {
  if (props.theme === 'brand') return colorVars.groupBrandBg
  if (props.theme && props.theme !== 'default') return props.theme
  return colorVars.groupDefaultBg
})

const contentMaxHeight = computed(() => {
  if (props.maxHeight == null || collapsed.value) return undefined
  const headerH = isTitleOnTop.value ? measuredHeaderHeight.value : 0
  const verticalPadding = 8 + 10
  const verticalGap = isTitleOnTop.value ? CARD_GAP : 0
  return Math.max(props.maxHeight - headerH - verticalPadding - verticalGap, 0)
})

function measureContent() {
  if (contentRef.value) {
    contentHeight.value = contentRef.value.scrollHeight
  }
}
function measureHeader() {
  if (headerRef.value) {
    measuredHeaderHeight.value = headerRef.value.offsetHeight
  }
}

// 用 rAF 去抖:合并同一帧内 onUpdated 的多次触发为一次测量,
// 避免在更新钩子中同步读取 scrollHeight/offsetHeight 造成强制重排(layout thrashing)。
let measureRafId: number | null = null
function scheduleMeasure() {
  if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
    // SSR / 无 rAF 环境降级为同步测量
    measureContent()
    measureHeader()
    return
  }
  if (measureRafId != null) return
  measureRafId = window.requestAnimationFrame(() => {
    measureRafId = null
    measureContent()
    measureHeader()
  })
}

onMounted(() => {
  measureContent()
  measureHeader()
})
onUpdated(() => {
  scheduleMeasure()
})
onBeforeUnmount(() => {
  if (measureRafId != null && typeof window !== 'undefined') {
    window.cancelAnimationFrame(measureRafId)
    measureRafId = null
  }
})

// manage inert attribute for collapsed content
watch(
  [collapsed, hasScrollableContent],
  ([isCollapsed, isScrollable]) => {
    nextTick(() => {
      const element = isScrollable
        ? contentScrollbarRef.value?.wrapRef ?? null
        : contentViewportRef.value
      if (!element) return
      if (isCollapsed) {
        element.setAttribute('inert', '')
      } else {
        element.removeAttribute('inert')
      }
    })
  },
  { immediate: true },
)

const groupContainerStyle = computed<CSSProperties>(() => ({
  display: 'inline-flex',
  flexDirection: isTitleOnTop.value ? 'column' : 'row',
  gap: '0px',
  alignItems: !isTitleOnTop.value || shouldFillWidth.value ? 'stretch' : 'flex-start',
  width: shouldFillWidth.value ? '100%' : 'fit-content',
  maxWidth: props.maxWidth ? `${props.maxWidth}px` : undefined,
  maxHeight: props.maxHeight ? `${props.maxHeight}px` : undefined,
  background: background.value,
  borderRadius: colorVars.radiusCard,
  padding: collapsed.value ? '8px 10px' : '8px 10px 10px',
  boxSizing: 'border-box',
  transition: 'padding 0.2s ease',
}))

const contentViewportStyle = computed<CSSProperties>(() => ({
  maxHeight: collapsed.value
    ? '0px'
    : contentMaxHeight.value != null
      ? `${Math.min(contentHeight.value, contentMaxHeight.value)}px`
      : `${contentHeight.value}px`,
  opacity: collapsed.value ? 0 : 1,
  marginTop: isTitleOnTop.value ? (collapsed.value ? '0px' : `${CARD_GAP}px`) : '0px',
  marginLeft: isTitleOnTop.value ? '0px' : collapsed.value ? '0px' : `${CARD_GAP}px`,
  transition: 'max-height 0.24s ease, opacity 0.2s ease, margin 0.24s ease',
  pointerEvents: collapsed.value ? 'none' : undefined,
  flex: props.maxWidth != null || shouldFillWidth.value ? 1 : undefined,
  minWidth: props.maxWidth != null || shouldFillWidth.value ? 0 : undefined,
  width: isTitleOnTop.value && (props.maxWidth != null || shouldFillWidth.value) ? '100%' : undefined,
  maxWidth: !isTitleOnTop.value && props.maxWidth != null ? '100%' : undefined,
}))

const contentScrollbarWrapStyle = computed<CSSProperties>(() => ({
  maxHeight: contentViewportStyle.value.maxHeight,
  overflowX: props.maxHeight != null && props.maxWidth == null ? 'hidden' : undefined,
  overflowY: props.maxWidth != null && props.maxHeight == null ? 'hidden' : undefined,
}))

// ── provide group context ──
provide(statisticGroupKey, { inGroup: true })

// ── provide appearance context (replaces React cloneElement) ──
provide(statisticCardAppearanceKey, {
  appearance: computed<StatisticCardAppearanceProps>(() => ({
    size: props.size,
    reverseColor: props.reverseColor,
    border: props.border,
    // Vue 对 Boolean 类型 prop 未传入时会 casting 为 false，会覆盖下游 `?? true` 默认链。
    // 这里把隐式/显式的 false 归一为 undefined 放行，使 formula/base 的等高默认(true)生效。
    equalHeight: props.equalHeight ? true : undefined,
    numberScroll: props.numberScroll,
    subIndicatorsLayout: props.subIndicatorsLayout,
  })),
})
</script>
